import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/salon/Navbar';
import BookingModal from '@/components/salon/BookingModal';

import WhatsAppButton from '@/components/salon/WhatsAppButton';
import GoogleMap from '@/components/salon/GoogleMap';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import CookieBanner from '@/components/CookieBanner';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowRight, Scissors, Sparkles, CalendarPlus, Phone, ChevronLeft, ChevronRight } from 'lucide-react';
import { DEFAULT_HERO_IMAGE, getHeroSlides } from '@/heroSlides';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';

const API = '/api';

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState({});
  const [activeHeroSlide, setActiveHeroSlide] = useState(0);
  const [heroPaused, setHeroPaused] = useState(false);
  const heroSlides = getHeroSlides(settings);

  useEffect(() => {
    setActiveHeroSlide((current) => current < heroSlides.length ? current : 0);
  }, [heroSlides.length]);

  useEffect(() => {
    if (heroSlides.length < 2 || heroPaused || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const timer = window.setInterval(() => setActiveHeroSlide((current) => (current + 1) % heroSlides.length), 5000);
    return () => window.clearInterval(timer);
  }, [heroSlides.length, heroPaused]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedBookingService, setSelectedBookingService] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadPageData = async () => {
      await Promise.all([
        fetchServices(),
        fetchSettings(),
        fetchGallery(),
      ]);

      if (isMounted) {
        setIsPageLoading(false);
      }
    };

    loadPageData();

    return () => {
      isMounted = false;
    };
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings', error);
    }
  };

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setGalleryImages(response.data);
    } catch (error) {
      console.error('Failed to fetch gallery', error);
    }
  };

  const handleBookNow = () => {
    if (!user) {
      toast.info('Bitte registrieren Sie sich oder melden Sie sich an, um einen Termin zu buchen.');
      navigate('/login?redirect=booking');
      return;
    }
    setIsBookingModalOpen(true);
  };

  const handleServiceBookNow = (service) => {
    if (!user) {
      toast.info('Bitte registrieren Sie sich oder melden Sie sich an, um einen Termin zu buchen.');
      navigate(`/login?redirect=booking&service=${encodeURIComponent(service.id)}`);
      return;
    }
    setSelectedBookingService(service);
    setIsBookingModalOpen(true);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!user || params.get('booking') !== 'open') {
      return;
    }

    const serviceId = params.get('service');
    if (serviceId && services.length === 0) {
      return;
    }

    setSelectedBookingService(serviceId ? services.find(service => service.id === serviceId) || null : null);
    setIsBookingModalOpen(true);
    navigate('/', { replace: true });
  }, [location.search, navigate, services, user]);

  // Default gallery images (used if no images in database)
  const defaultGalleryImages = [
    'https://images.unsplash.com/photo-1637777277337-f114350fb088?w=800&q=80',
    'https://images.unsplash.com/photo-1653124783585-70c4b7d949c7?w=800&q=80',
    'https://images.unsplash.com/photo-1629397683830-9805395892e8?w=800&q=80',
    'https://images.unsplash.com/photo-1701977082153-4ac2c0e9899d?w=800&q=80',
  ];

  // Use database images if available, otherwise use defaults
  const displayGalleryImages = galleryImages.length > 0 
    ? galleryImages.map(img => img.image_url.startsWith('http') ? img.image_url : `/api${img.image_url}`)
    : defaultGalleryImages;

  const isOnRequestService = (service) => Number(service.price) === 0;
  const isWomenService = (service) => (
    service.category === 'women'
    || (
      !service.category
      && isOnRequestService(service)
      && !/(dreadlocks?|retwist)/i.test(service.name || '')
    )
  );
  const serviceGroups = [
    {
      key: 'barber',
      title: 'Herren & Barber',
      subtitle: 'Präzise Cuts, Fades, Bartpflege und ausgewählte Spezialservices.',
      services: services.filter(service => !isWomenService(service)),
    },
    {
      key: 'women',
      title: 'Damenbereich',
      subtitle: 'Waschen, Schneiden, Umformung, Coloration und Pflege – transparent mit festen Preisen oder Ab-Preisen.',
      services: services.filter(isWomenService),
    },
  ].filter(group => group.services.length > 0);

  const formatServicePrice = (price) => new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(price));

  if (isPageLoading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main
          className="min-h-[calc(100vh-64px)] bg-gradient-to-br from-blue-50 to-white flex items-center justify-center"
          aria-busy="true"
          aria-label="Salon Galo wird geladen"
        >
          <div className="w-10 h-10 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section - Bento Grid */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 h-auto md:h-[600px]">
            {/* Banner slideshow: existing hero_image remains the first slide until changed in admin. */}
            <div className="md:col-span-8 relative h-[420px] sm:h-[520px] md:h-full rounded-3xl overflow-hidden group"
              onMouseEnter={() => setHeroPaused(true)}
              onMouseLeave={() => setHeroPaused(false)}
              onFocusCapture={() => setHeroPaused(true)}
              onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setHeroPaused(false); }}
              aria-label="Salon Galo Bilder-Slideshow"
            >
              {heroSlides.map((slide, index) => (
                <img
                  key={`${slide}-${index}`}
                  src={slide.startsWith('http') ? slide : `/api${slide}`}
                  alt={`Salon Galo – Bild ${index + 1} von ${heroSlides.length}`}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${index === activeHeroSlide ? 'opacity-100' : 'opacity-0'}`}
                  aria-hidden={index !== activeHeroSlide}
                  onError={(event) => {
                    if (event.currentTarget.src !== DEFAULT_HERO_IMAGE) event.currentTarget.src = DEFAULT_HERO_IMAGE;
                  }}
                />
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 p-8 sm:p-12 z-10">
                <Button
                  onClick={handleBookNow}
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-6 text-base shadow-xl hover:shadow-blue-200 transition-all"
                  data-testid="hero-book-button"
                >
                  <Scissors className="w-5 h-5 mr-2" />
                  Jetzt buchen
                </Button>
              </div>
              {heroSlides.length > 1 && (
                <>
                  <button type="button" aria-label="Vorheriges Bannerbild" onClick={() => setActiveHeroSlide((current) => (current - 1 + heroSlides.length) % heroSlides.length)}
                    className="absolute z-20 left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/40 hover:bg-black/60 p-2 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white">
                    <ChevronLeft aria-hidden="true" className="h-6 w-6" />
                  </button>
                  <button type="button" aria-label="Nächstes Bannerbild" onClick={() => setActiveHeroSlide((current) => (current + 1) % heroSlides.length)}
                    className="absolute z-20 right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/40 hover:bg-black/60 p-2 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white">
                    <ChevronRight aria-hidden="true" className="h-6 w-6" />
                  </button>
                  <div className="absolute z-20 bottom-4 right-4 flex gap-2" aria-label="Bannerbild auswählen">
                    {heroSlides.map((_, index) => (
                      <button key={index} type="button" aria-label={`Bannerbild ${index + 1} anzeigen`}
                        aria-current={index === activeHeroSlide ? 'true' : undefined}
                        onClick={() => setActiveHeroSlide(index)}
                        className={`h-2.5 rounded-full transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-white ${index === activeHeroSlide ? 'w-7 bg-white' : 'w-2.5 bg-white/60 hover:bg-white'}`} />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Side Cards */}
            <div className="md:col-span-4 flex flex-col gap-4">
              <div className="flex-1 bg-white p-6 rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold tracking-tight text-slate-900">
                    Premium Services
                  </h3>
                </div>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> 1 Getränk kostenlos
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> Kaffee kostenlos
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> Ohne Wartezeit
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> Vorher/Nachher Bild oder Video
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> Premium Kunden Karte
                  </li>
                </ul>
              </div>
              <button 
                onClick={handleBookNow}
                className="flex-1 bg-gradient-to-br from-blue-600 to-blue-700 p-8 rounded-3xl shadow-lg cursor-pointer hover:from-blue-700 hover:to-blue-800 hover:shadow-xl transition-all transform hover:scale-[1.02] text-left"
                data-testid="online-booking-card"
              >
                <h3 className="text-2xl font-semibold text-white mb-2">Online Buchung</h3>
                <p className="text-blue-100 leading-relaxed">
                  Registrieren, anmelden und den Termin bequem per E-Mail bestätigen lassen.
                </p>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <span className="inline-flex items-center rounded-full bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 mb-5">
              Salon Galo Leistungen
            </span>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 mb-4">
              Services für Herren und Damen
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Klar getrennt, schnell gefunden und passend zu Ihrem gewünschten Styling.
            </p>
          </div>

          <div className="space-y-12">
            {serviceGroups.map((group) => {
              const GroupIcon = group.key === 'women' ? Sparkles : Scissors;
              const isWomenGroup = group.key === 'women';

              return (
                <div
                  key={group.key}
                  className={`rounded-3xl border p-5 sm:p-8 ${
                    isWomenGroup
                      ? 'border-rose-100 bg-gradient-to-br from-rose-50/80 via-white to-amber-50/60'
                      : 'border-slate-200 bg-slate-50/70'
                  }`}
                  data-testid={`service-group-${group.key}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-7">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                        isWomenGroup ? 'bg-rose-100 text-rose-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        <GroupIcon className="w-6 h-6" aria-hidden="true" />
                      </div>
                      <div>
                        <h3 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
                          {group.title}
                        </h3>
                        <p className="text-slate-600 mt-1 max-w-2xl">{group.subtitle}</p>
                      </div>
                    </div>
                    {isWomenGroup && (
                      <span className="self-start sm:self-center rounded-full bg-white border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-700 shadow-sm">
                        Persönliche Beratung
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {group.services.map((service) => {
                      const isOnRequest = isOnRequestService(service);
                      return (
                        <Card
                          key={service.id}
                          className={`service-card bg-white p-5 rounded-2xl border shadow-sm hover:-translate-y-1 hover:shadow-lg transition-all duration-300 ${
                            isWomenGroup ? 'border-rose-100' : 'border-slate-100'
                          } ${service.coming_soon ? 'relative overflow-hidden' : ''}`}
                          data-testid={`service-card-${service.id}`}
                        >
                          {service.coming_soon && (
                            <div className="absolute top-4 right-4 z-10">
                              <span className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                                Coming Soon
                              </span>
                            </div>
                          )}
                          <div className={`mb-4 rounded-xl overflow-hidden flex items-center justify-center ${
                            isWomenGroup ? 'bg-rose-950' : 'bg-black'
                          } ${service.coming_soon ? 'opacity-60' : ''}`}>
                            <img
                              src={service.image_url || 'https://customer-assets.emergentagent.com/job_492e7c8b-48f5-4b35-b44f-b67bd2d22f37/artifacts/1u3pu4qf_IMG_5702.PNG'}
                              alt={service.name}
                              className="w-full h-36 object-contain p-3"
                            />
                          </div>
                          {isWomenGroup && service.subcategory && (
                            <span className="inline-flex mb-2 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700">
                              {service.subcategory}
                            </span>
                          )}
                          <h4 className="text-lg font-semibold tracking-tight text-slate-900 mb-1">
                            {service.name}
                          </h4>
                          <p className="text-sm text-slate-600 mb-4 leading-relaxed min-h-10">
                            {service.description || 'Professioneller Service'}
                          </p>
                          <div className="mb-4">
                            {isOnRequest ? (
                              <div>
                                <span className="text-lg font-bold text-amber-700">Preis auf Anfrage</span>
                                <p className="text-xs text-slate-500 mt-1">Termin nach persönlicher Absprache</p>
                              </div>
                            ) : (
                              <div className="flex items-end gap-2">
                                <span className={`text-2xl font-bold ${service.coming_soon ? 'text-slate-400' : isWomenGroup ? 'text-rose-700' : 'text-blue-600'}`}>
                                  {service.price_is_from ? 'ab ' : ''}€{formatServicePrice(service.price)}
                                </span>
                                <span className="text-xs text-slate-500 pb-1">{service.duration} Min</span>
                              </div>
                            )}
                          </div>
                          {isOnRequest ? (
                            <Button
                              onClick={() => {
                                const msg = encodeURIComponent(`Hallo! Ich interessiere mich für ${service.name}. Können Sie mir bitte Informationen zu Preis und Termin geben?`);
                                window.open(`https://wa.me/491636849710?text=${msg}`, '_blank', 'noopener,noreferrer');
                              }}
                              className="w-full bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`service-contact-${service.id}`}
                            >
                              <Phone className="w-4 h-4 mr-2" />
                              Über WhatsApp anfragen
                            </Button>
                          ) : (
                            <Button
                              onClick={() => handleServiceBookNow(service)}
                              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                              disabled={service.coming_soon}
                              data-testid={`service-book-${service.id}`}
                            >
                              <CalendarPlus className="w-4 h-4 mr-2" />
                              Jetzt buchen
                            </Button>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={handleBookNow}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8"
              data-testid="services-book-button"
            >
              Termin buchen
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Gallery Teaser */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 mb-4">
              Unsere Arbeiten
            </h2>
            <p className="text-lg text-slate-600">Einblicke in unseren Salon und unsere Kreationen</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {displayGalleryImages.slice(0, 4).map((img, idx) => (
              <div
                key={idx}
                className="aspect-square rounded-2xl overflow-hidden group cursor-pointer"
                data-testid={`gallery-teaser-${idx}`}
                onClick={() => navigate('/gallery')}
              >
                <img
                  src={img}
                  alt={`Gallery ${idx + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/gallery')}
              variant="outline"
              size="lg"
              className="rounded-full border-slate-200 hover:bg-slate-50"
              data-testid="view-gallery-button"
            >
              Zur Galerie
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      {(settings.about_title || settings.about_text) && (
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 mb-4">
                {settings.about_title || 'Über uns'}
              </h2>
            </div>
            <div className="max-w-4xl mx-auto">
              <p className="text-lg text-slate-600 leading-relaxed text-center whitespace-pre-line">
                {settings.about_text}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Location Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 mb-4">
              Besuchen Sie uns
            </h2>
            <p className="text-lg text-slate-600">
              {settings.salon_address || 'Wir freuen uns auf Ihren Besuch'}
            </p>
          </div>
          <GoogleMap />
        </div>
      </section>

      {/* Brand Carousel */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">Marken und Co.</h2>
          <div className="relative overflow-hidden">
            <div className="flex animate-scroll">
              {/* First set of logos */}
              <div className="flex space-x-12 px-6">
                <div className="flex-shrink-0 w-40 h-24 flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_hairdash-2/artifacts/k9z20f8r_6AF3ABF9-7E7B-445E-8402-ECEF51A17060.jpg" 
                    alt="Marke 1" 
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <div className="flex-shrink-0 w-40 h-24 flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_hairdash-2/artifacts/7oxyg7s0_Axe_logo_2016.svg" 
                    alt="Axe" 
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <div className="flex-shrink-0 w-40 h-24 flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_hairdash-2/artifacts/psy770l3_gummy.jpg" 
                    alt="Gummy" 
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <div className="flex-shrink-0 w-40 h-24 flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_hairdash-2/artifacts/b992rc7s_IMG_1516.jpeg" 
                    alt="Marke 4" 
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <div className="flex-shrink-0 w-40 h-24 flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_hairdash-2/artifacts/hstdh9fv_PHOTO-2025-06-16-17-19-33.jpg" 
                    alt="Marke 5" 
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              </div>
              {/* Duplicate set for seamless loop */}
              <div className="flex space-x-12 px-6">
                <div className="flex-shrink-0 w-40 h-24 flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_hairdash-2/artifacts/k9z20f8r_6AF3ABF9-7E7B-445E-8402-ECEF51A17060.jpg" 
                    alt="Marke 1" 
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <div className="flex-shrink-0 w-40 h-24 flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_hairdash-2/artifacts/7oxyg7s0_Axe_logo_2016.svg" 
                    alt="Axe" 
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <div className="flex-shrink-0 w-40 h-24 flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_hairdash-2/artifacts/psy770l3_gummy.jpg" 
                    alt="Gummy" 
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <div className="flex-shrink-0 w-40 h-24 flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_hairdash-2/artifacts/b992rc7s_IMG_1516.jpeg" 
                    alt="Marke 4" 
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <div className="flex-shrink-0 w-40 h-24 flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_hairdash-2/artifacts/hstdh9fv_PHOTO-2025-06-16-17-19-33.jpg" 
                    alt="Marke 5" 
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_friseur-booking/artifacts/mtmj2lif_IMG_5702.PNG" 
                alt="Salon Galo Logo" 
                className="h-12 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-slate-400">Ihr Salon für perfektes Styling</p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Öffnungszeiten</h3>
              <div className="text-slate-400 text-sm whitespace-pre-line">
                {settings.opening_hours || 'Di-Fr: 11:30-18:00\nSa: 10:00-16:00\nSo-Mo: Geschlossen'}
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Kontakt</h3>
              <div className="text-slate-400 text-sm space-y-2">
                {settings.phone_number && (
                  <p>Tel: {settings.phone_number}</p>
                )}
                {settings.email && (
                  <p>E-Mail: {settings.email}</p>
                )}
                {settings.salon_address && (
                  <p>{settings.salon_address}</p>
                )}
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Folgen Sie uns</h3>
              <div className="flex space-x-4">
                {settings.instagram_url && (
                  <a 
                    href={settings.instagram_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-slate-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors"
                    data-testid="instagram-link"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                )}
                {settings.facebook_url && (
                  <a 
                    href={settings.facebook_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-slate-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors"
                    data-testid="facebook-link"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                )}
                {settings.tiktok_url && (
                  <a
                    href={settings.tiktok_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-slate-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors"
                    data-testid="tiktok-link"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </a>
                )}
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
              <div className="flex flex-col sm:flex-row items-center gap-4 text-sm">
                <p className="text-slate-400">© 2025 Salon Galo. Alle Rechte vorbehalten.</p>
                <div className="flex items-center gap-4">
                  <Link to="/impressum" className="text-slate-400 hover:text-white transition-colors">
                    Impressum
                  </Link>
                  <span className="text-slate-600">|</span>
                  <Link to="/privacy-policy" className="text-slate-400 hover:text-white transition-colors">
                    Datenschutz
                  </Link>
                  <span className="text-slate-600">|</span>
                  <Link to="/cookie-policy" className="text-slate-400 hover:text-white transition-colors">
                    Cookies
                  </Link>
                </div>
              </div>
              <button
                onClick={() => navigate('/admin/login')}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg text-sm font-medium transition-all border border-slate-700 hover:border-slate-600"
                data-testid="admin-login-footer"
              >
                🔐 Admin Bereich
              </button>
            </div>
          </div>
        </div>
      </footer>

      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => {
          setIsBookingModalOpen(false);
          setSelectedBookingService(null);
        }}
        services={services}
        initialSelectedService={selectedBookingService}
      />

      
      <WhatsAppButton />
      <CookieBanner />
    </div>
  );
};

export default HomePage;
